/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformServer } from '@angular/common';
import {
  HttpContextToken,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID, signal } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthRepository } from 'src/app/routes/auth/state/auth.repository';
import { AuthService } from 'src/app/routes/auth/state/auth.service';
import { environment } from 'src/environments/environment';
import { EcommerceOutputLogin } from '../types/auth.types';
import { ErrorMessages } from '../utils/consts/enums';

export const INTERCEPT_ERRORS = new HttpContextToken(() => true);
export const INTERCEPT_NO_ERROR = new HttpContextToken(() => false);

const isRefreshing = signal(false);
const refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

export function authInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const authRepository = inject(AuthRepository);
  const authService = inject(AuthService);
  const toastService = inject(HotToastService);
  const translateService = inject(TranslateService);
  const platformId = inject(PLATFORM_ID);

  const setHeaders = (url: string): Observable<Record<string, any>> => {
    return authRepository.token$.pipe(
      take(1),
      switchMap((token) => {
        const headers: Record<string, any> = {};
        headers['ecommerceId'] = environment.ecommerceInstanceId;
        headers['currentLang'] = translateService.currentLang ?? 'pl';

        if (
          url.includes(environment.apiUrl) &&
          !url.includes('/auth/refresh-token') &&
          token
        ) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        return of(headers);
      })
    );
  };

  const logoutUser = (err: any): Observable<HttpEvent<any>> => {
    authService.logout();
    return throwError(() => err);
  };

  const sendRefreshTokenRequest = (
    err: any,
    req: HttpRequest<any>,
    refreshToken: string
  ): Observable<HttpEvent<any>> => {
    isRefreshing.set(true);
    refreshTokenSubject.next(null);

    return authService.refreshToken(refreshToken).pipe(
      switchMap((response: EcommerceOutputLogin) => {
        authRepository.setToken(response.accessToken);
        authRepository.setRefreshToken(response.refreshToken);
        isRefreshing.set(false);
        refreshTokenSubject.next(true);

        const newHeaders = {
          Authorization: `Bearer ${response.accessToken}`,
        };

        return next(req.clone({ setHeaders: newHeaders }));
      }),
      catchError(() => {
        isRefreshing.set(false);
        refreshTokenSubject.next(false);
        return logoutUser(err);
      })
    );
  };

  const holdRequestsWhenIsRefreshing = (
    err: any,
    req: HttpRequest<any>
  ): Observable<HttpEvent<any>> => {
    return refreshTokenSubject.pipe(
      filter((data: boolean | null) => data != null),
      take(1),
      switchMap((data: boolean | null) => {
        if (data) {
          return setHeaders(req.url).pipe(
            switchMap((headers: Record<string, any>) =>
              next(req.clone({ setHeaders: headers }))
            )
          );
        } else {
          return throwError(() => err);
        }
      })
    );
  };

  const proceedWithRefreshToken = (
    err: any,
    req: HttpRequest<any>
  ): Observable<HttpEvent<any>> => {
    return authRepository.refreshToken$.pipe(
      switchMap((refreshToken: string | null) => {
        if (!refreshToken) {
          return logoutUser(err);
        } else if (!isRefreshing()) {
          return sendRefreshTokenRequest(err, req, refreshToken);
        } else {
          return holdRequestsWhenIsRefreshing(err, req);
        }
      })
    );
  };

  return setHeaders(request.url).pipe(
    switchMap((headers: Record<string, any>) =>
      next(request.clone({ setHeaders: headers }))
    ),
    catchError((err: any) => {
      if (isPlatformServer(platformId)) {
        return throwError(() => err);
      }

      const interceptErrors = request.context.get(INTERCEPT_ERRORS);
      const interceptNoError = request.context.get(INTERCEPT_NO_ERROR);
      console.log('interceptNoError', interceptNoError);

      if (interceptNoError) {
        return throwError(() => err);
      }

      if (err.status === 401 && err.statusText === 'Unauthorized') {
        const message = err.error?.message;

        if (
          message === 'CUSTOMER_ACCOUNT_NOT_ACTIVATED' ||
          message === 'LOGIN_BAD_CREDENTIALS' ||
          message === 'CHANGE_PASSWORD_BAD_CREDENTIALS'
        ) {
          toastService.warning(
            translateService.instant(
              ErrorMessages[message as keyof typeof ErrorMessages]
            )
          );
          return throwError(() => err);
        }

        return proceedWithRefreshToken(err, request);
      } else if (interceptErrors && err.status >= 400 && err.status < 500) {
        const errorMessage = Array.isArray(err.error?.message)
          ? err.error.message[0]
          : err.error?.message;
        const errorDetails = Array.isArray(err.error?.error)
          ? err.error.error[0]
          : err.error?.error;

        const translatedError = translateService.instant(
          ErrorMessages[errorMessage as keyof typeof ErrorMessages] ??
            ErrorMessages['UNKNOWN_ERROR']
        );

        toastService.warning(
          errorMessage === 'ENTITY_DUPLICATE_FOUND' ||
            errorMessage === 'UNPLANED_TECHNICAL_BREAK_STARTED'
            ? `${translatedError}\n${errorDetails}`
            : translatedError
        );
      } else if (interceptErrors) {
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            const jsonError = JSON.parse(reader.result as string);
            toastService.error(
              jsonError.message === 'NO_CHROME'
                ? translateService.instant(ErrorMessages['NO_CHROME'])
                : translateService.instant(
                    ErrorMessages['INTERNAL_SERVER_ERROR']
                  )
            );
          };
          reader.readAsText(err.error);
        } else if (err.error.message !== 'TICKETS_NOT_PRINTED') {
          toastService.error(
            translateService.instant(ErrorMessages['INTERNAL_SERVER_ERROR'])
          );
        }
      }

      return throwError(() => err);
    })
  );
}
